import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import DashLayout from '../../layouts/dashboard/dashLayout';
import { useAppSelector } from '../../stores';
import {
    getFlag,
    acknowledgeFlag,
    resolveFlag,
    AndonFlag,
} from '../../services/api/andonService';
import './andon.css';

function statusBadgeClass(status: string): string {
    const key = (status || 'pending').toLowerCase();
    const known = ['pending', 'notified', 'acknowledged', 'resolved', 'dismissed'];
    return known.includes(key) ? `andon-badge andon-badge--${key}` : 'andon-badge andon-badge--pending';
}

const AndonDetail: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams<{ id: string }>();
    const users = useAppSelector((state) => state.auth);

    const [flag, setFlag] = useState<AndonFlag | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [resolveNote, setResolveNote] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!id || !users.access_token) return;

        const load = async () => {
            setLoading(true);
            setLoadError(null);
            try {
                const data = await getFlag(id, users.access_token);
                setFlag(data);
            } catch (err) {
                const message =
                    err instanceof Error && err.message
                        ? err.message
                        : t('andon.detail.loadError', 'Failed to load flag details');
                setLoadError(message);
                toast.error(message);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [id, users.access_token, t]);

    const handleAcknowledge = useCallback(async () => {
        if (!id || !flag || submitting) return;
        setSubmitting(true);
        try {
            await acknowledgeFlag(id, {}, users.access_token);
            setFlag((prev) => (prev ? { ...prev, status: 'acknowledged' } : prev));
            toast.success(t('andon.detail.acknowledgeSuccess', 'Flag acknowledged'));
        } catch {
            toast.error(t('andon.detail.acknowledgeError', 'Failed to acknowledge'));
        } finally {
            setSubmitting(false);
        }
    }, [id, flag, submitting, users.access_token, t]);

    const handleResolve = useCallback(async () => {
        if (!id || !flag || submitting) return;
        if (!resolveNote.trim()) {
            toast.error(t('andon.detail.resolveNoteRequired', 'Please add a resolution note'));
            return;
        }
        setSubmitting(true);
        try {
            await resolveFlag(id, { resolutionNotes: resolveNote.trim() }, users.access_token);
            setFlag((prev) =>
                prev ? { ...prev, status: 'resolved', resolutionNote: resolveNote.trim() } : prev
            );
            toast.success(t('andon.detail.resolveSuccess', 'Flag resolved'));
        } catch {
            toast.error(t('andon.detail.resolveError', 'Failed to resolve'));
        } finally {
            setSubmitting(false);
        }
    }, [id, flag, submitting, resolveNote, users.access_token, t]);

    const formatDate = (iso?: string) => {
        if (!iso) return '—';
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch {
            return iso;
        }
    };

    return (
        <DashLayout>
            <div className="andon-page andon-page--detail">
                <Link to="/dashboard/andon" className="andon-page__back">
                    ← {t('andon.detail.backToList', 'Back to Andon Hub')}
                </Link>

                <h1 className="andon-page__title">
                    {t('andon.detail.title', 'Flag Details')}
                </h1>

                {loading ? (
                    <p className="andon-page__muted">{t('andon.detail.loading', 'Loading...')}</p>
                ) : !flag ? (
                    <p className="andon-page__muted">
                        {loadError || t('andon.detail.notFound', 'Flag not found.')}
                    </p>
                ) : (
                    <>
                        <div className="andon-card">
                            <div className="andon-status-row">
                                <span className="andon-label">{t('andon.detail.status', 'Status')}</span>
                                <span className={statusBadgeClass(flag.status)}>
                                    {t(`andon.hub.status.${flag.status}`, flag.status)}
                                </span>
                            </div>

                            <div className="andon-field">
                                <span className="andon-label">{t('andon.detail.origin', 'Origin (host app)')}</span>
                                <span className="andon-value">{flag.hostApp}</span>
                            </div>

                            <div className="andon-field">
                                <span className="andon-label">{t('andon.detail.user', 'Reported by')}</span>
                                <span className="andon-value">
                                    {flag.userName} ({flag.userEmail})
                                </span>
                            </div>

                            <div className="andon-field">
                                <span className="andon-label">{t('andon.detail.message', 'Message')}</span>
                                <p className="andon-body">{flag.userMessage}</p>
                            </div>

                            {flag.contextText && (
                                <div className="andon-field">
                                    <span className="andon-label">{t('andon.detail.context', 'Context')}</span>
                                    <p className="andon-body andon-body--secondary">{flag.contextText}</p>
                                </div>
                            )}

                            {flag.subject && (
                                <div className="andon-field">
                                    <span className="andon-label">{t('andon.detail.subject', 'Subject')}</span>
                                    <p className="andon-body">{flag.subject}</p>
                                </div>
                            )}
                        </div>

                        <div className="andon-card">
                            <span className="andon-label">{t('andon.detail.timestamps', 'Timestamps')}</span>
                            <div className="andon-timestamps">
                                <div>
                                    <span className="andon-label andon-label--sm">
                                        {t('andon.detail.created', 'Created')}
                                    </span>
                                    <span className="andon-value">
                                        {formatDate(flag.createdAt || flag.created_at)}
                                    </span>
                                </div>
                                {flag.acknowledgedAt && (
                                    <div>
                                        <span className="andon-label andon-label--sm">
                                            {t('andon.detail.acknowledged', 'Acknowledged')}
                                        </span>
                                        <span className="andon-value">{formatDate(flag.acknowledgedAt)}</span>
                                    </div>
                                )}
                                {flag.resolvedAt && (
                                    <div>
                                        <span className="andon-label andon-label--sm">
                                            {t('andon.detail.resolved', 'Resolved')}
                                        </span>
                                        <span className="andon-value">{formatDate(flag.resolvedAt)}</span>
                                    </div>
                                )}
                            </div>
                            {(flag.notifiedAdmins !== undefined || flag.notifiedAdminsCount !== undefined) && (
                                <div className="andon-field" style={{ marginTop: 12, marginBottom: 0 }}>
                                    <span className="andon-label">
                                        {t('andon.detail.notified', 'Notifications sent')}
                                    </span>
                                    <span className="andon-value">
                                        {flag.notifiedAdmins ?? flag.notifiedAdminsCount}
                                    </span>
                                </div>
                            )}
                        </div>

                        {flag.resolutionNote && (
                            <div className="andon-card">
                                <span className="andon-label">
                                    {t('andon.detail.resolutionNote', 'Resolution note')}
                                </span>
                                <p className="andon-body">{flag.resolutionNote}</p>
                            </div>
                        )}

                        {flag.status !== 'resolved' && (
                            <div className="andon-card">
                                <div className="andon-actions">
                                    {(flag.status === 'pending' || flag.status === 'notified') && (
                                        <button
                                            type="button"
                                            className="andon-btn andon-btn--outline"
                                            onClick={handleAcknowledge}
                                            disabled={submitting}
                                        >
                                            {t('andon.detail.acknowledgeBtn', 'Acknowledge')}
                                        </button>
                                    )}

                                    <div className="andon-actions__resolve">
                                        <textarea
                                            className="andon-textarea"
                                            value={resolveNote}
                                            onChange={(e) => setResolveNote(e.target.value)}
                                            placeholder={t(
                                                'andon.detail.resolveNotePlaceholder',
                                                'Add a resolution note...'
                                            )}
                                            rows={2}
                                        />
                                        <button
                                            type="button"
                                            className="andon-btn andon-btn--primary"
                                            onClick={handleResolve}
                                            disabled={submitting || !resolveNote.trim()}
                                        >
                                            {t('andon.detail.resolveBtn', 'Resolve')}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </DashLayout>
    );
};

export default AndonDetail;
